import { store } from './store.js';
import { safeSetHTML } from './security.js';

/**
 * Problem Editor UI
 * Handles the No-Code Editor Interface
 */

const ProblemEditor = {
    modal: null,

    init() {
        this.renderModal();
        this.modal = document.getElementById('problemEditorModal');
        this.populateContexts();
    },

    renderModal() {
        // Remove existing modal if it exists to ensure we have the latest HTML
        const existingModal = document.getElementById('problemEditorModal');
        if (existingModal) {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="problemEditorModal" class="editor-modal hidden">
            <div class="editor-overlay" id="editorOverlay"></div>
            <div class="editor-container">
                <header class="editor-header">
                    <h2>✏️ Editor de Problemas</h2>
                    <button class="btn-close-editor" id="btnCloseEditorX">✕</button>
                </header>
                
                <div class="editor-body">
                    <form id="problemEditorForm">
                        <!-- Row 1: Context & Level -->
                        <div class="form-row">
                            <div class="form-group">
                                <label>Contexto:</label>
                                <select id="editorContext" required>
                                    <option value="">-- Seleccionar --</option>
                                    <!-- Populated dynamically -->
                                </select>
                            </div>
                            <div class="form-group">
                                <label>Nivel:</label>
                                <select id="editorLevel" required>
                                    <option value="beginner">APRENDIZ (Fácil)</option>
                                    <option value="advanced">AVANZADO (Medio)</option>
                                    <option value="expert">EXPERTO (Difícil)</option>
                                </select>
                            </div>
                        </div>

                        <!-- Row 2: Type & Auto-Generate -->
                        <div class="form-row">
                            <div class="form-group">
                                <label>Operación:</label>
                                <select id="editorType" required>
                                    <option value="sum">SUMA</option>
                                    <option value="subtraction">RESTA</option>
                                </select>
                            </div>
                        </div>

                        <!-- Statement -->
                        <div class="form-group">
                            <label>Enunciado del Problema:</label>
                            <textarea id="editorStatement" rows="4" required 
                                placeholder="Ej: En la granja hay 5 vacas y llegan 3 más. ¿Cuántas vacas hay en total?"></textarea>
                            <div class="input-hint">Escribe números (dígitos) y objetos claros. La pregunta debe terminar en '?'</div>
                        </div>

                        <!-- Auto-detected Data Preview -->
                        <div id="editorAnalysis" class="analysis-box">
                            <p><strong>Datos detectados:</strong> <span id="detectedNumbers">-</span></p>
                            <p><strong>Objetos:</strong> <span id="detectedObjects">-</span></p>
                            <p><strong>Pregunta:</strong> <span id="detectedQuestion">-</span></p>
                            <p><strong>Solución calculada:</strong> <span id="calculatedSolution">-</span></p>
                        </div>

                        <div class="editor-actions-container">
                            <div class="editor-actions-main">
                                <button type="button" class="btn btn-secondary" id="btnCancelEditor" style="flex: 1;">CANCELAR</button>
                                <button type="submit" class="btn btn-primary" id="btnSaveProblem" style="flex: 1;">💾 GUARDAR PROBLEMA</button>
                            </div>
                            <div class="editor-actions-secondary">
                                <input type="file" id="importExcelInput" accept=".xlsx, .xls" style="display: none;">
                                <button type="button" class="btn" id="btnExportExcel" style="background: #ffcc80; color: #5d4037; flex: 1; font-size: 0.9rem;">📤 EXPORTAR EXCEL</button>
                                <button type="button" class="btn" id="btnImportExcel" style="background: #C8E6C9; color: #2e7d32; flex: 1; font-size: 0.9rem;">📥 IMPORTAR EXCEL</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        `;

        // Safe insertion of modal HTML
        const modalContainer = document.createElement('div');
        safeSetHTML(modalContainer, modalHTML);
        const modalElement = modalContainer.firstElementChild;
        document.body.appendChild(modalElement);

        // Attach event listeners manually (to bypass DOMPurify stripping inline onclicks)
        const overlay = modalElement.querySelector('#editorOverlay');
        const closeX = modalElement.querySelector('#btnCloseEditorX');
        const cancelBtn = modalElement.querySelector('#btnCancelEditor');
        const exportBtn = modalElement.querySelector('#btnExportExcel');
        const importBtn = modalElement.querySelector('#btnImportExcel');
        const importInput = modalElement.querySelector('#importExcelInput');
        const form = modalElement.querySelector('#problemEditorForm');
        const statement = modalElement.querySelector('#editorStatement');
        const contextSelect = modalElement.querySelector('#editorContext');

        if (overlay) overlay.onclick = () => this.close(true);
        if (closeX) closeX.onclick = () => this.close(true);
        if (cancelBtn) cancelBtn.onclick = () => this.close(true);
        if (exportBtn) exportBtn.onclick = () => this.exportExcel();
        if (importBtn) importBtn.onclick = () => this.importExcel();
        if (importInput) {
            importInput.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                    CustomProblemManager.importFromExcel(file)
                        .then(count => {
                            alert(`✅ ¡Éxito! Se han importado ${count} problemas.`);
                            if (window.ExcelLoader) ExcelLoader.injectCustomProblems();
                        })
                        .catch(err => alert(`❌ Error: ${err}`));
                }
            };
        }
        if (statement) statement.oninput = () => this.analyzeStatement();
        if (contextSelect) contextSelect.onchange = () => this.analyzeStatement();

        if (form) {
            form.onsubmit = (e) => {
                e.preventDefault();
                this.save();
            };
        }
    },

    populateContexts() {
        const select = document.getElementById('editorContext');
        if (!select || !window.ExcelLoader) return;

        safeSetHTML(select, '<option value="">-- Seleccionar --</option>');

        Object.entries(ExcelLoader.CONTEXT_TO_SHEET).forEach(([key, name]) => {
            const option = document.createElement('option');
            option.value = key;
            option.textContent = name;
            select.appendChild(option);
        });
    },

    open() {
        if (!this.modal) this.init();
        this.modal.classList.remove('hidden');
        document.getElementById('problemEditorForm').reset();
        this.analyzeStatement(); // Reset analysis
    },

    close(forceHome = false) {
        if (this.modal) this.modal.classList.add('hidden');
        if (forceHome && window.resetApp) {
            window.resetApp();
        }
    },

    analyzeStatement() {
        const text = document.getElementById('editorStatement').value;
        const analysis = window.ExcelLoader ? ExcelLoader.analyzeProblemStatement(text) : { isValid: false };
        const question = window.ExcelLoader ? ExcelLoader.extractQuestion(text) : '-';

        document.getElementById('detectedNumbers').textContent = analysis.numbers ? analysis.numbers.join(', ') : '-';
        document.getElementById('detectedObjects').textContent = analysis.names ? analysis.names.join(', ') : '-';
        document.getElementById('detectedQuestion').textContent = question !== 'undefined?' ? question : '-';
        document.getElementById('calculatedSolution').textContent = analysis.expectedAnswer !== undefined ? analysis.expectedAnswer : '-';

        // Auto-select type based on analysis or keywords
        if (analysis.expectedAnswer !== undefined) {
            const typeSelect = document.getElementById('editorType');
            const nums = analysis.numbers;
            if (nums.length >= 2) {
                if (analysis.expectedAnswer > nums[0]) typeSelect.value = 'sum';
                else typeSelect.value = 'subtraction';
            }
        }
    },

    save() {
        const context = document.getElementById('editorContext').value;
        const level = document.getElementById('editorLevel').value;
        const type = document.getElementById('editorType').value;
        const statement = document.getElementById('editorStatement').value;

        if (!context || !level || !type || !statement) {
            alert('Por favor, completa todos los campos.');
            return;
        }

        const problemData = {
            contextId: context,
            level: level,
            type: type,
            statement: statement
        };

        if (window.CustomProblemManager) {
            const savedProblem = CustomProblemManager.saveProblem(problemData);
            alert('¡Problema guardado con éxito! Disponible en el menú PROFES.');
            this.close();
        } else {
            alert('Error: CustomProblemManager no encontrado.');
        }
    },

    launchProblem(customProblem) {
        if (!window.SessionManager || !window.ExcelLoader) return;

        // Convert format
        const internalProblem = CustomProblemManager.convertToInternalFormat(customProblem);

        // Update session config to match problem
        if (store.config) {
            store.config.problemType = internalProblem.type;
            store.config.level = internalProblem.level;
            store.config.context = internalProblem.contextId;
        }

        // Setup session state
        window.SessionManager.state.problems = [internalProblem];
        window.SessionManager.state.currentProblem = 0;
        window.SessionManager.state.answers = {};
        window.SessionManager.state.score = 0;

        // Start
        window.SessionManager.showNarrativeIntro();
    },

    verifyTeacherAccess() {
        if (!window.UserManager) return true;
        const pin = prompt('Introduce el PIN de PROFESOR para esta acción:');
        if (pin === window.UserManager.getTeacherPin()) {
            return true;
        } else {
            alert('❌ PIN incorrecto.');
            return false;
        }
    },

    exportExcel() {
        if (!this.verifyTeacherAccess()) return;
        if (window.CustomProblemManager) {
            CustomProblemManager.exportToExcel();
        }
    },

    importExcel() {
        if (!this.verifyTeacherAccess()) return;
        const input = document.getElementById('importExcelInput');
        if (input) input.click();
    }
};

window.ProblemEditor = ProblemEditor;

// Auto-init on load
document.addEventListener('DOMContentLoaded', () => {
    // Delay slightly to ensure ExcelLoader is ready for context population
    setTimeout(() => ProblemEditor.init(), 1000);
});
